import axios from 'axios';
import { API_CONFIG, buildApiUrl, getTimeout } from '../config/apiConfig';

class AuthService {
  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    this.timeout = getTimeout('default');
    this.token = localStorage.getItem('auth_token');
    this.refreshToken = localStorage.getItem('refresh_token');
  }

  // Configurar token nos headers
  setAuthHeaders() {
    if (this.token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`;
    }
  }

  // Remover token dos headers
  clearAuthHeaders() {
    delete axios.defaults.headers.common['Authorization'];
  }

  // Fazer login
  async login(credentials) {
    try {
      console.log('🔐 Tentando fazer login...');
      
      const response = await axios.post(
        buildApiUrl(API_CONFIG.ENDPOINTS.LOGIN),
        credentials,
        {
          headers: API_CONFIG.DEFAULT_HEADERS,
          timeout: this.timeout
        }
      );

      const d = response.data || {};
      const h = response.headers || {};
      const headerAuth = h['authorization'] || h['Authorization'];
      const headerToken = headerAuth && headerAuth.startsWith('Bearer ') ? headerAuth.substring(7) : null;
      const token =
        d.token ||
        d.accessToken ||
        d.access_token ||
        (d.data && (d.data.token || d.data.access_token)) ||
        (d.data && d.data.accessToken) ||
        (d.authorization && d.authorization.token) ||
        headerToken;
      const refreshToken =
        d.refreshToken ||
        d.refresh_token ||
        d.refreshToken ||
        (d.data && (d.data.refreshToken || d.data.refresh_token)) ||
        (d.data && d.data.refreshToken) ||
        d.refresh_token ||
        (d.authorization && d.authorization.refreshToken);

      if (token) {
        this.token = token;
        this.refreshToken = refreshToken;
        
        localStorage.setItem('auth_token', this.token);
        if (this.refreshToken) {
          localStorage.setItem('refresh_token', this.refreshToken);
        }
        
        this.setAuthHeaders();
        
        console.log('✅ Login realizado com sucesso');
        
        return {
          success: true,
          token: this.token,
          refreshToken: this.refreshToken,
          user: d.user || d.data?.user || d.userData || d,
          message: 'Login realizado com sucesso'
        };
      } else {
        throw new Error('Token não recebido da API');
      }
    } catch (error) {
      console.error('❌ Erro no login:', error.message);
      
      let errorMessage = 'Erro ao fazer login';
      
      if (error.response) {
        console.log('🔍 Detalhes da resposta:', API_CONFIG.ENDPOINTS.LOGIN);
        const status = error.response.status;
        const data = error.response.data;
        
        switch (status) {
          case 401:
            errorMessage = data.message || 'Credenciais inválidas';
            break;
          case 403:
            errorMessage = data.message || 'Acesso negado';
            break;
          case 404:
            errorMessage = 'Endpoint de login não encontrado';
            break;
          case 500:
            errorMessage = 'Erro interno do servidor';
            break;
          default:
            errorMessage = data.message || `Erro ${status}`;
        }
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Timeout: Servidor demorou para responder';
      } else if (error.request) {
        errorMessage = 'Erro de conexão: Não foi possível conectar ao servidor';
      }
      
      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  // Fazer logout
  async logout() {
    try {
      console.log('🚪 Fazendo logout...');
      
      // Tentar fazer logout no servidor
      if (this.token) {
        try {
          await axios.post(
            buildApiUrl(API_CONFIG.ENDPOINTS.LOGOUT),
            {},
            {
              headers: {
                ...API_CONFIG.DEFAULT_HEADERS,
                'Authorization': `Bearer ${this.token}`
              },
              timeout: getTimeout('health_check')
            }
          );
        } catch (error) {
          console.warn('⚠️ Erro ao fazer logout no servidor:', error.message);
        }
      }
      
      // Limpar dados locais
      this.clearAuthData();
      
      console.log('✅ Logout realizado com sucesso');
      
      return {
        success: true,
        message: 'Logout realizado com sucesso'
      };
    } catch (error) {
      console.error('❌ Erro no logout:', error.message);
      
      // Mesmo com erro, limpar dados locais
      this.clearAuthData();
      
      return {
        success: true,
        message: 'Logout realizado (com erro no servidor)'
      };
    }
  }

  // Limpar dados de autenticação
  clearAuthData() {
    this.token = null;
    this.refreshToken = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    this.clearAuthHeaders();
  }

  // Verificar se está autenticado
  isAuthenticated() {
    return !!this.token && this.token !== 'null' && this.token !== 'undefined';
  }

  // Obter token atual
  getToken() {
    return this.token;
  }

  // Obter perfil do usuário
  async getProfile() {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('Usuário não autenticado');
      }

      const profileUrl = buildApiUrl(API_CONFIG.ENDPOINTS.PROFILE);
      console.debug('AUTH getProfile URL', { url: profileUrl });
      const response = await axios.get(
        profileUrl,
        {
          headers: {
            ...API_CONFIG.DEFAULT_HEADERS,
            'Authorization': `Bearer ${this.token}`
          },
          timeout: this.timeout
        }
      );

      return {
        success: true,
        user: response.data,
        message: 'Perfil carregado com sucesso'
      };
    } catch (error) {
      const status = error?.response?.status;
      const url = error?.config?.url || buildApiUrl(API_CONFIG.ENDPOINTS.PROFILE);
      const code = error?.code;
      console.error('❌ Erro ao obter perfil', { url, status, code, message: error?.message, response: error?.response?.data });
      
      let errorMessage = 'Erro ao carregar perfil';
      
      if (error.response) {
        const status = error.response.status;
        if (status === 401) {
          // Token expirado, fazer logout
          this.clearAuthData();
          errorMessage = 'Sessão expirada. Faça login novamente.';
        } else {
          errorMessage = error.response.data?.message || `Erro ${status}`;
        }
      }
      
      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  // Renovar token
  async refreshAuthToken() {
    try {
      if (!this.refreshToken) {
        throw new Error('Refresh token não disponível');
      }

      const response = await axios.post(
        buildApiUrl(API_CONFIG.ENDPOINTS.REFRESH),
        { refreshToken: this.refreshToken, refresh_token: this.refreshToken },
        {
          headers: API_CONFIG.DEFAULT_HEADERS,
          timeout: this.timeout
        }
      );

      const d = response.data || {};
      const h = response.headers || {};
      const headerAuth = h['authorization'] || h['Authorization'];
      const headerToken = headerAuth && headerAuth.startsWith('Bearer ') ? headerAuth.substring(7) : null;
      const token =
        d.token ||
        d.accessToken ||
        d.access_token ||
        (d.data && (d.data.token || d.data.access_token || d.data.accessToken)) ||
        headerToken;
      const refreshToken =
        d.refreshToken ||
        d.refresh_token ||
        (d.data && (d.data.refreshToken || d.data.refresh_token)) ||
        this.refreshToken;

      if (token) {
        this.token = token;
        this.refreshToken = refreshToken;
        
        localStorage.setItem('auth_token', this.token);
        if (this.refreshToken) {
          localStorage.setItem('refresh_token', this.refreshToken);
        }
        
        this.setAuthHeaders();
        
        return {
          success: true,
          token: this.token,
          message: 'Token renovado com sucesso'
        };
      } else {
        throw new Error('Novo token não recebido');
      }
    } catch (error) {
      console.error('❌ Erro ao renovar token:', error.message);
      
      // Se falhar, limpar dados e forçar novo login
      this.clearAuthData();
      
      return {
        success: false,
        error: 'Sessão expirada. Faça login novamente.',
        message: 'Sessão expirada. Faça login novamente.'
      };
    }
  }

  // Testar conexão com API de autenticação
  async testAuthConnection() {
    try {
      const response = await axios.get(
        buildApiUrl(API_CONFIG.ENDPOINTS.HEALTH),
        {
          headers: API_CONFIG.DEFAULT_HEADERS,
          timeout: getTimeout('health_check')
        }
      );

      return {
        success: true,
        status: response.status,
        message: 'Conexão com API de autenticação estabelecida',
        url: buildApiUrl(API_CONFIG.ENDPOINTS.HEALTH)
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Não foi possível conectar à API de autenticação',
        url: buildApiUrl(API_CONFIG.ENDPOINTS.HEALTH)
      };
    }
  }

  // Inicializar serviço (verificar token salvo)
  initialize() {
    const savedToken = localStorage.getItem('auth_token');
    const savedRefreshToken = localStorage.getItem('refresh_token');
    
    if (savedToken && savedToken !== 'null' && savedToken !== 'undefined') {
      this.token = savedToken;
      this.refreshToken = savedRefreshToken;
      this.setAuthHeaders();
      console.log('🔐 Token de autenticação carregado do localStorage');
    }
  }
}

// Criar instância singleton
const authService = new AuthService();

// Inicializar automaticamente
authService.initialize();

export default authService;

